import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { StoragePlace } from '../storage-place/storage-place';
import { StoragePlaceService } from '../storage-place/storage-place.service';
import { StockService } from '../stock/stock.service';
import { BestellungExtern } from '../../wa-wi/bestellung-extern/bestellung-extern';
import { BestellungExternService } from '../../wa-wi/bestellung-extern/bestellung-extern.service';
import { Material } from '../material/material';
import { GoodReceipt } from './good-receipt';
import {
    ArticleScanAutocompleteComponent,
    ArticleScanSelectedEvent,
} from '../../components/article-scan-autocomplete/article-scan-autocomplete.component';
import { MatDialog } from '@angular/material/dialog';
import { GoodsReceiptAddArticleComponent } from './goods-receipt-add-article/goods-receipt-add-article.component';
import { MessageService } from '../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { KieneUtils } from '../../../../projects/kiene-core/src/lib/utils/KieneUtils';

@Component({
    selector: 'kiene-goods-receipt',
    templateUrl: './goods-receipt.component.html',
    styleUrls: ['./goods-receipt.component.scss'],
})
export class GoodsReceiptComponent implements AfterViewInit {
    storagePlaceFormCtrl: UntypedFormControl;
    materialien: Observable<Material[]>;
    storagePlaces: Observable<StoragePlace[]>;
    possibleBestellungen: BestellungExtern[] = [];

    newMaterial: Material = new Material();
    goodReceipt: GoodReceipt = new GoodReceipt();
    storagePlaceSuggestions: StoragePlace[] = [];
    selectedStoragePlace: StoragePlace = new StoragePlace();
    showStoragePlaceAutocomplete = false;

    selectedOrder: BestellungExtern = new BestellungExtern();

    saveButtonDisabled = false;
    produktkennzeichnung: string;

    @ViewChild('materialAutocomplete', { static: false })
    materialAutocomplete: ArticleScanAutocompleteComponent;

    constructor(
        private readonly bestellungExternService: BestellungExternService,
        private readonly storagePlaceService: StoragePlaceService,
        private readonly stockService: StockService,
        private readonly notification: MessageService,
        private readonly dialog: MatDialog,
    ) {
        this.autocompleteStoragePlace();
    }

    ngAfterViewInit() {}

    isArticleInDatabase(event: ArticleScanSelectedEvent) {
        if (event.article) {
            this.materialSelected(event);
        } else {
            this.openAddArticleDialog();
        }
    }

    materialSelected(event: ArticleScanSelectedEvent) {
        this.newMaterial = event.article;
        this.goodReceipt.artikel_id = event.article.artikel_id;
        if (event.mhd) {
            this.goodReceipt.mhd = event.mhd;
        }
        if (event.charge) {
            this.goodReceipt.charge = event.charge;
        }
        this.getStoragePlaceSuggestions(event.article.artikel_id);
        this.loadPossibleOrders(this.newMaterial.artikel_id);
    }

    autocompleteStoragePlace() {
        this.storagePlaceFormCtrl = new UntypedFormControl();
        this.storagePlaces = this.storagePlaceFormCtrl.valueChanges.pipe(
            startWith(''),
            switchMap(value => this.searchStoragePlaces(value)),
        );
    }

    searchStoragePlaces(value: string) {
        return this.storagePlaceService.searchStoragePlaces(value).pipe(map(response => response.records));
    }

    getStoragePlaceSuggestions(artikelId: number) {
        if (artikelId) {
            this.storagePlaceService.getStoragePlacesForMaterial(artikelId).subscribe({
                next: response => {
                    this.storagePlaceSuggestions = response.records;
                },
                error: error => {
                    this.notification.errorMessage('Fehler!', error);
                },
            });
        }
    }

    setSelectedStoragePlace(storagePlace: StoragePlace) {
        if (KieneUtils.sameId(this.selectedStoragePlace.lagerplatz_id, storagePlace.lagerplatz_id)) {
            this.selectedStoragePlace = new StoragePlace();
        } else {
            this.selectedStoragePlace = storagePlace;
        }
    }

    isStoragePlaceSelected(storagePlace: StoragePlace) {
        return KieneUtils.sameId(this.selectedStoragePlace.lagerplatz_id, storagePlace.lagerplatz_id);
    }

    isOrderSelected(po: BestellungExtern) {
        return KieneUtils.sameId(this.selectedOrder.bestellung_extern_id, po.bestellung_extern_id);
    }

    setSelectedOrder(po: BestellungExtern) {
        this.selectedOrder = po;
    }

    loadPossibleOrders(materialId: number) {
        const status: number[] = [23, 24, 25, 27, 29];
        this.bestellungExternService.getBestellungenByMaterialAndStatus(materialId, status).subscribe(response => {
            this.possibleBestellungen = response.records;
        });
    }

    saveGoodReceipt() {
        this.saveButtonDisabled = true;
        this.goodReceipt.lagerplatz_id_ziel = this.selectedStoragePlace.lagerplatz_id;
        this.goodReceipt.bestellung_extern_id = this.selectedOrder.bestellung_extern_id;
        this.stockService.createStock(this.goodReceipt).subscribe(
            () => {
                this.notification.infoMessage('Der neue Bestand wurde erfolgreich gespeichert!');
                this.clearInputs();
                this.saveButtonDisabled = false;
            },
            error => {
                this.notification.errorMessage(
                    'Ein Fehler ist aufgetreten! Der neue Bestand konnte nicht gespeichert werden!',
                    error,
                );
                this.saveButtonDisabled = false;
            },
        );
    }

    clearInputs() {
        this.newMaterial = undefined;
        this.storagePlaceFormCtrl.setValue(undefined);
        this.storagePlaceSuggestions = [];
        this.selectedOrder = new BestellungExtern();
        this.goodReceipt = new GoodReceipt();
        this.selectedStoragePlace = new StoragePlace();
    }

    setProduktkennzeichnung(pk: string) {
        this.produktkennzeichnung = pk;
    }

    openAddArticleDialog() {
        const pk = this.produktkennzeichnung || this.materialAutocomplete.getValue();
        if (!pk) {
            this.notification.infoMessage('Keine alternative Produktbezeichnung eingetragen!');
        } else {
            this.dialog
                .open(GoodsReceiptAddArticleComponent, { data: pk, minWidth: '400px' })
                .afterClosed()
                .subscribe({
                    next: (artikel: Material) => {
                        if (artikel) {
                            const event = new ArticleScanSelectedEvent();
                            event.article = artikel;
                            this.materialSelected(event);
                        }
                    },
                });
        }
    }

    reload() {
        this.clearInputs();
    }
}
